<template>
  <div class="regra-condicional">
    <div class="header-info">
      <fa-icon icon="info-circle" />
      As regras são avaliadas em ordem; a primeira que casar define o valor enviado.
    </div>

    <draggable
      :list="regra.casos"
      handle=".drag-handle"
      animation="200"
      ghost-class="regra-bloco--dragging"
      @end="emit"
    >
    <div v-for="(caso, ci) in regra.casos" :key="caso._uid || `caso-${ci}`" class="regra-bloco">
      <div class="regra-header">
        <span class="drag-handle" v-if="regra.casos.length > 1" title="Arraste para reordenar">
          <fa-icon icon="grip-vertical" />
        </span>
        <span class="regra-titulo">Regra {{ ci + 1 }}</span>
        <div class="modo-segmented">
          <button
            type="button"
            class="segmented-btn"
            :class="{ 'segmented-btn--active': !isAvancado(ci) }"
            @click="desativarAvancado(ci)"
          >
            Simples (E)
          </button>
          <button
            type="button"
            class="segmented-btn"
            :class="{ 'segmented-btn--active': isAvancado(ci) }"
            @click="ativarAvancado(ci)"
          >
            Avançado (E/OU)
          </button>
        </div>
        <CButton
          color="danger"
          variant="ghost"
          size="sm"
          class="btn-icon header-trash"
          v-if="regra.casos.length > 1"
          @click="removerCaso(ci)"
          title="Remover regra" aria-label="Remover regra"
        >
          <fa-icon icon="trash-alt" />
        </CButton>
      </div>

      <div class="quando-bloco">
        <div class="bloco-label">Quando</div>

        <!-- Modo simples: AND linear de condições -->
        <div v-if="!isAvancado(ci)" class="cond-lista">
          <div
            v-for="(cond, ki) in caso.condicao.grupos[0].condicoes"
            :key="cond._uid || `cond-${ci}-${ki}`"
            class="cond-linha"
          >
            <span class="cond-conector">{{ ki === 0 ? '' : 'E' }}</span>
            <select class="form-control form-control-sm cond-campo" v-model="cond.campo">
              <option value="">Campo da Verso</option>
              <option v-for="c in opcoesComValorAtual(campos, cond.campo)" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
            <select class="form-control form-control-sm cond-op" :value="cond.operador" @change="alterarOperadorCondicao(cond, $event.target.value)">
              <option v-for="op in operadores" :key="op.value" :value="op.value">{{ op.label }}</option>
            </select>
            <select
              class="form-control form-control-sm cond-tipo-valor"
              :class="{ 'is-invalid': exigeTipoValor(cond) && !cond.tipo_valor }"
              :value="exigeTipoValor(cond) ? (cond.tipo_valor || '') : ''"
              :disabled="!exigeTipoValor(cond)"
              :title="tituloTipoValor(cond)"
              @change="alterarTipoValorCondicao(cond, $event.target.value)"
            >
              <option value="" disabled>{{ exigeTipoValor(cond) ? 'Tipo do valor' : 'Não aplicável' }}</option>
              <option v-if="cond.tipo_valor && !tipoValorConhecido(cond.tipo_valor)" :value="cond.tipo_valor">{{ cond.tipo_valor }}</option>
              <option v-for="t in tiposValor" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
            <select
              v-if="comparaComCampo(cond)"
              class="form-control form-control-sm cond-valor"
              :value="cond.valor"
              @change="atualizarCampoComparado(cond, $event.target.value)"
            >
              <option value="">Campo da Verso</option>
              <option v-for="c in opcoesComValorAtual(campos, cond.valor)" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
            <input
              v-else
              class="form-control form-control-sm cond-valor"
              type="text"
              :value="valorParaInput(cond)"
              :disabled="operadorSemValor(cond.operador)"
              :placeholder="placeholderValor(cond.operador)"
              :title="tituloValor(cond.operador)"
              @input="atualizarValorCondicao(cond, $event.target.value)"
            />
            <CButton
              color="danger"
              variant="ghost"
              size="sm"
              class="btn-icon"
              v-if="caso.condicao.grupos[0].condicoes.length > 1"
              @click="removerCondicaoSimples(ci, ki)"
              title="Remover condição" aria-label="Remover condição"
            >
              <fa-icon icon="times" />
            </CButton>
          </div>
          <CButton color="primary" variant="ghost" size="sm" class="btn-add-inline" @click="adicionarCondicaoSimples(ci)">
            <fa-icon icon="plus" /> Adicionar condição (E)
          </CButton>
        </div>

        <!-- Modo avançado: grupos com AND/OR aninhados -->
        <div v-else class="modo-avancado">
          <div
            v-for="(grupo, gi) in caso.condicao.grupos"
            :key="grupo._uid || `grupo-${ci}-${gi}`"
            class="grupo-bloco"
          >
            <div class="grupo-header">
              <span class="grupo-prefix">{{ gi === 0 ? 'Atende' : 'E também atende' }}</span>
              <select
                class="form-control form-control-sm logica-select"
                :value="grupo.logica"
                @change="alterarLogica(ci, gi, $event.target.value)"
              >
                <option value="AND">todas as condições</option>
                <option value="OR">qualquer uma das condições</option>
              </select>
              <CButton
                color="danger"
                variant="ghost"
                size="sm"
                class="btn-icon"
                v-if="caso.condicao.grupos.length > 1"
                @click="removerGrupo(ci, gi)"
                title="Remover grupo" aria-label="Remover grupo de condições"
              >
                <fa-icon icon="times" />
              </CButton>
            </div>

            <div
              v-for="(cond, ki) in grupo.condicoes"
              :key="cond._uid || `cond-${ci}-${gi}-${ki}`"
              class="cond-linha"
            >
              <span class="cond-conector">
                {{ ki === 0 ? '' : (grupo.logica === 'AND' ? 'E' : 'OU') }}
              </span>
              <select class="form-control form-control-sm cond-campo" v-model="cond.campo">
                <option value="">Campo da Verso</option>
                <option v-for="c in opcoesComValorAtual(campos, cond.campo)" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
              <select class="form-control form-control-sm cond-op" :value="cond.operador" @change="alterarOperadorCondicao(cond, $event.target.value)">
                <option v-for="op in operadores" :key="op.value" :value="op.value">{{ op.label }}</option>
              </select>
              <select
                class="form-control form-control-sm cond-tipo-valor"
                :class="{ 'is-invalid': exigeTipoValor(cond) && !cond.tipo_valor }"
                :value="exigeTipoValor(cond) ? (cond.tipo_valor || '') : ''"
                :disabled="!exigeTipoValor(cond)"
                :title="tituloTipoValor(cond)"
                @change="alterarTipoValorCondicao(cond, $event.target.value)"
              >
                <option value="" disabled>{{ exigeTipoValor(cond) ? 'Tipo do valor' : 'Não aplicável' }}</option>
                <option v-if="cond.tipo_valor && !tipoValorConhecido(cond.tipo_valor)" :value="cond.tipo_valor">{{ cond.tipo_valor }}</option>
                <option v-for="t in tiposValor" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
              <select
                v-if="comparaComCampo(cond)"
                class="form-control form-control-sm cond-valor"
                :value="cond.valor"
                @change="atualizarCampoComparado(cond, $event.target.value)"
              >
                <option value="">Campo da Verso</option>
                <option v-for="c in opcoesComValorAtual(campos, cond.valor)" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
              <input
                v-else
                class="form-control form-control-sm cond-valor"
                type="text"
                :value="valorParaInput(cond)"
                :disabled="operadorSemValor(cond.operador)"
                :placeholder="placeholderValor(cond.operador)"
                :title="tituloValor(cond.operador)"
                @input="atualizarValorCondicao(cond, $event.target.value)"
              />
              <CButton
                color="danger"
                variant="ghost"
                size="sm"
                class="btn-icon"
                v-if="grupo.condicoes.length > 1"
                @click="removerCondicao(ci, gi, ki)"
                title="Remover condição" aria-label="Remover condição"
              >
                <fa-icon icon="times" />
              </CButton>
            </div>

            <CButton color="primary" variant="ghost" size="sm" class="btn-add-inline" @click="adicionarCondicao(ci, gi)">
              <fa-icon icon="plus" /> Adicionar condição
            </CButton>
          </div>

          <CButton color="primary" variant="ghost" size="sm" class="btn-add-inline" @click="adicionarGrupo(ci)">
            <fa-icon icon="plus" /> Adicionar outro grupo (E)
          </CButton>
        </div>
      </div>

      <div class="enviar-bloco">
        <div class="bloco-label">Enviar</div>
        <div
          v-for="(saida, si) in getSaidas(caso.entao)"
          :key="`saida-${ci}-${si}`"
          class="de-para-linha"
        >
          <div class="de-para-col de-para-col--de">
            <span class="de-para-label" title="Valor a ser enviado (literal ou campo da Verso)">Valor</span>
            <div class="de-para-input-wrap">
              <ExpressaoValor
                :value="saida.expressao"
                :campos="campos"
                :invalid="mostrarErros && !expressaoTemValor(saida.expressao)"
                @input="atualizarSaidaExpressao(ci, si, $event)"
              />
            </div>
          </div>
          <div class="de-para-seta">
            <fa-icon icon="arrow-right" />
          </div>
          <div class="de-para-col de-para-col--para">
            <span class="de-para-label" title="Campo de destino no ERP">Destino</span>
            <div class="de-para-input-wrap">
              <input
                class="form-control form-control-sm"
                :class="{ 'is-invalid': mostrarErros && !paraDoSaida(caso.entao, si) }"
                type="text"
                :value="paraDoSaida(caso.entao, si)"
                :placeholder="placeholderPara"
                @input="atualizarSaidaPara(ci, si, $event.target.value)"
              />
              <div class="acoes-para">
                <CButton
                  v-if="possuiCamposPara && si === 0 && !isMultiSaida(caso.entao)"
                  color="primary"
                  variant="ghost"
                  size="sm"
                  class="btn-selecionar-inline"
                  @click="$emit('selecionar-para', $event)"
                >
                  <fa-icon icon="list" /> Selecionar da lista
                </CButton>
                <CButton
                  v-if="isMultiSaida(caso.entao) && getSaidas(caso.entao).length > 1"
                  color="danger"
                  variant="ghost"
                  size="sm"
                  class="btn-icon"
                  @click="removerSaidaCaso(ci, si)"
                  title="Remover destino" aria-label="Remover destino"
                >
                  <fa-icon icon="times" />
                </CButton>
              </div>
            </div>
          </div>
        </div>
        <CButton
          color="primary"
          variant="ghost"
          size="sm"
          class="btn-add-inline btn-add-saida"
          @click="adicionarSaidaCaso(ci)"
        >
          <fa-icon icon="plus" /> Adicionar destino
        </CButton>
      </div>
    </div>
    </draggable>

    <div class="acoes-casos">
      <CButton
        color="primary"
        variant="outline"
        size="sm"
        class="btn-dashed"
        @click="adicionarCaso"
      >
        <fa-icon icon="plus" /> Adicionar regra
      </CButton>
      <CButton
        v-if="regra.casos.length > 0"
        color="secondary"
        variant="outline"
        size="sm"
        @click="duplicarUltimoCaso"
        title="Duplica a última regra com todos os campos preenchidos"
      >
        <fa-icon icon="copy" /> Duplicar regra
      </CButton>
    </div>

    <CButton
      v-if="!mostrarFallback"
      color="secondary"
      variant="outline"
      size="sm"
      class="btn-fallback-add"
      @click="ativarFallback"
    >
      <fa-icon icon="plus" /> Adicionar fallback (quando nenhuma regra casar)
    </CButton>

    <div v-if="mostrarFallback" class="fallback-bloco">
      <div class="fallback-header">
        <span class="fallback-label">
          <fa-icon icon="level-down-alt" />
          Senão — quando nenhuma regra casar
        </span>
        <CButton
          color="danger"
          variant="ghost"
          size="sm"
          class="btn-icon"
          @click="removerFallback"
          title="Remover fallback" aria-label="Remover fallback"
        >
          <fa-icon icon="trash-alt" />
        </CButton>
      </div>
      <div
        v-for="(saida, si) in getSaidas(regra.senao)"
        :key="`saida-senao-${si}`"
        class="de-para-linha"
      >
        <div class="de-para-col de-para-col--de">
          <span class="de-para-label" title="Valor a ser enviado (literal ou campo da Verso)">Valor</span>
          <div class="de-para-input-wrap">
            <ExpressaoValor
              :value="saida.expressao"
              :campos="campos"
              @input="atualizarSaidaSenaoExpressao(si, $event)"
            />
          </div>
        </div>
        <div class="de-para-seta">
          <fa-icon icon="arrow-right" />
        </div>
        <div class="de-para-col de-para-col--para">
          <span class="de-para-label" title="Campo de destino no ERP">Destino</span>
          <div class="de-para-input-wrap">
            <input
              class="form-control form-control-sm"
              type="text"
              :value="paraDoSaida(regra.senao, si)"
              :placeholder="placeholderPara"
              @input="atualizarSaidaSenaoPara(si, $event.target.value)"
            />
            <div class="acoes-para">
              <CButton
                v-if="possuiCamposPara && si === 0 && !isMultiSaida(regra.senao)"
                color="primary"
                variant="ghost"
                size="sm"
                class="btn-selecionar-inline"
                @click="$emit('selecionar-para', $event)"
              >
                <fa-icon icon="list" /> Selecionar da lista
              </CButton>
              <CButton
                v-if="isMultiSaida(regra.senao) && getSaidas(regra.senao).length > 1"
                color="danger"
                variant="ghost"
                size="sm"
                class="btn-icon"
                @click="removerSaidaSenao(si)"
                title="Remover destino" aria-label="Remover destino"
              >
                <fa-icon icon="times" />
              </CButton>
            </div>
          </div>
        </div>
      </div>
      <CButton
        color="primary"
        variant="ghost"
        size="sm"
        class="btn-add-inline btn-add-saida"
        @click="adicionarSaidaSenao"
      >
        <fa-icon icon="plus" /> Adicionar destino
      </CButton>
    </div>
  </div>
</template>

<script>
import ExpressaoValor from "./ExpressaoValor";
import draggable from "vuedraggable";
import { TIPOS_VALOR_CONDICAO, tipoValorConhecido, opcoesComValorAtual, operadorSemValor, exigeTipoValor } from "./tipoValorCondicao";

// UID interno para v-keys estáveis em listas reordenáveis (draggable).
// Sem isso, `v-for :key="ci"` faz Vue reusar componentes errados após drag.
const uid = () => Math.random().toString(36).slice(2, 10);

const novaCondicao = () => ({
  _uid: uid(),
  campo: "",
  operador: "igual",
  valor: "",
  tipo_valor: "static",
});

const novoGrupo = () => ({
  _uid: uid(),
  logica: "AND",
  condicoes: [novaCondicao()],
});

const novoCaso = () => ({
  _uid: uid(),
  condicao: { grupos: [novoGrupo()] },
  entao: { tipo: "literal", valor: "" },
});

export const regraCondicionalVazia = () => ({
  tipo: "condicional",
  casos: [novoCaso()],
  senao: null,
});

export default {
  name: "RegraCondicional",
  components: { ExpressaoValor, draggable },
  props: {
    value: {
      type: Object,
      required: true,
      validator: (v) => v && v.tipo === "condicional" && Array.isArray(v.casos),
    },
    campos: {
      type: Array,
      default: () => [],
      validator: (arr) => arr.every((c) => c && typeof c === "object" && "value" in c && "label" in c),
    },
    operadores: {
      type: Array,
      default: () => [],
      validator: (arr) => arr.every((o) => o && typeof o === "object" && "value" in o && "label" in o),
    },
    paraValue: { type: String, default: "" },
    possuiCamposPara: { type: Boolean, default: false },
    placeholderPara: { type: String, default: "Campo de destino" },
    mostrarErros: { type: Boolean, default: false },
  },
  data() {
    return {
      tiposValor: TIPOS_VALOR_CONDICAO,
      avancadoPorCaso: {},
      mostrarFallback: this.fallbackTemConteudo(this.value && this.value.senao),
    };
  },
  computed: {
    regra() {
      return this.value;
    },
  },
  watch: {
    // deep: true para reagir a mutações internas (não só substituição de ref);
    // reset explícito quando senao = null garante UI consistente.
    "regra.senao": {
      handler(novoSenao) {
        this.mostrarFallback = this.fallbackTemConteudo(novoSenao);
      },
      deep: true,
    },
  },
  methods: {
    tipoValorConhecido,
    opcoesComValorAtual,
    exigeTipoValor,
    comparaComCampo(cond) {
      return cond.tipo_valor === "campo" && !this.operadorSemValor(cond.operador);
    },
    tituloTipoValor(cond) {
      if (!exigeTipoValor(cond)) return "Operadores 'é nulo'/'não nulo' não usam valor de comparação";
      if (!cond.tipo_valor) return "Escolha se o valor é fixo ou outro campo para poder salvar";
      return "Valor fixo compara com o texto digitado; Outro campo compara com o valor de um campo da Verso";
    },
    alterarTipoValorCondicao(cond, novoTipo) {
      const anterior = cond.tipo_valor;
      this.$set(cond, "tipo_valor", novoTipo);
      // Só limpa ao trocar entre tipos definidos: completar um tipo ausente
      // mantém o valor que já estava gravado.
      if (anterior && anterior !== novoTipo) {
        this.$set(cond, "valor", "");
      }
      this.emit();
    },
    atualizarCampoComparado(cond, caminho) {
      this.$set(cond, "valor", caminho);
      this.emit();
    },
    operadorSemValor,
    operadorMultiValor(op) {
      // operadores que esperam array de valores (separados por vírgula no input)
      return op === "em" || op === "nao_em";
    },
    placeholderValor(op) {
      if (this.operadorSemValor(op)) return "Não aplicável";
      if (this.operadorMultiValor(op)) return "Separe por vírgula: SP, RJ, MG";
      return "Valor";
    },
    tituloValor(op) {
      if (this.operadorSemValor(op)) return "Operadores 'é nulo'/'não nulo' não usam valor de comparação";
      if (this.operadorMultiValor(op)) return "Múltiplos valores separados por vírgula";
      return "";
    },
    valorParaInput(cond) {
      if (this.operadorMultiValor(cond.operador) && Array.isArray(cond.valor)) {
        return cond.valor.join(", ");
      }
      return cond.valor;
    },
    atualizarValorCondicao(cond, novoValor) {
      if (this.operadorMultiValor(cond.operador)) {
        // CSV → array, removendo vazios e trimando
        this.$set(cond, "valor", novoValor
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0));
      } else {
        this.$set(cond, "valor", novoValor);
      }
      this.emit();
    },
    alterarOperadorCondicao(cond, novoOperador) {
      const eraMulti = this.operadorMultiValor(cond.operador);
      const seraMulti = this.operadorMultiValor(novoOperador);
      this.$set(cond, "operador", novoOperador);

      // Converte valor ao mudar entre formas escalar ↔ array
      if (!eraMulti && seraMulti && cond.valor && !Array.isArray(cond.valor)) {
        // Era escalar virou multi: tenta dividir por vírgula
        const arr = String(cond.valor)
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        this.$set(cond, "valor", arr);
      } else if (eraMulti && !seraMulti && Array.isArray(cond.valor)) {
        // Era multi virou escalar: pega o primeiro item
        this.$set(cond, "valor", cond.valor[0] || "");
      }

      this.emit();
    },
    fallbackTemConteudo(senao) {
      if (!senao) return false;
      if (Array.isArray(senao)) {
        return senao.some(
          (s) => (s && s.para) || (s && s.expressao && s.expressao.valor)
        );
      }
      return !!senao.valor;
    },
    ativarFallback() {
      if (!this.regra.senao) {
        this.$set(this.regra, "senao", { tipo: "literal", valor: "" });
        this.emit();
      }
      this.mostrarFallback = true;
    },
    removerFallback() {
      if (!this.fallbackTemConteudo(this.regra.senao)) {
        this.aplicarRemocaoFallback();
        return;
      }
      this.confirmar("Remover o fallback desta regra?", "Os valores configurados no senão serão perdidos.")
        .then((ok) => { if (ok) this.aplicarRemocaoFallback(); });
    },
    aplicarRemocaoFallback() {
      this.$set(this.regra, "senao", null);
      this.mostrarFallback = false;
      this.emit();
    },
    casoTemConteudo(caso) {
      if (!caso) return false;
      const condicoes = (caso.condicao && caso.condicao.grupos || []).flatMap((g) => g.condicoes || []);
      const condicaoCheia = condicoes.some((c) => c.campo || (c.valor != null && c.valor !== ""));
      const entao = caso.entao;
      if (this.isMultiSaida(entao)) {
        return condicaoCheia || entao.some((s) => s.para || (s.expressao && s.expressao.valor));
      }
      return condicaoCheia || (entao && (entao.valor || entao.caminho));
    },
    confirmar(titulo, texto) {
      if (!window.swal) return Promise.resolve(true);
      return window.swal({
        title: titulo,
        text: texto,
        icon: "warning",
        buttons: { cancel: "Cancelar", confirm: { text: "Remover", className: "swal-button--danger" } },
        dangerMode: true,
      }).then((v) => !!v);
    },
    isAvancado(ci) {
      // avançado: forçado pelo usuário OU já tem mais de 1 grupo OU algum grupo é OR
      if (this.avancadoPorCaso[ci]) return true;
      const caso = this.regra.casos[ci];
      if (!caso) return false;
      if (caso.condicao.grupos.length > 1) return true;
      if (caso.condicao.grupos[0] && caso.condicao.grupos[0].logica === "OR") return true;
      return false;
    },
    ativarAvancado(ci) {
      this.$set(this.avancadoPorCaso, ci, true);
    },
    desativarAvancado(ci) {
      // simplifica: mantém apenas o primeiro grupo, força AND
      const caso = this.regra.casos[ci];
      if (caso.condicao.grupos.length > 1) {
        caso.condicao.grupos = [caso.condicao.grupos[0]];
      }
      caso.condicao.grupos[0].logica = "AND";
      this.$set(this.avancadoPorCaso, ci, false);
      this.emit();
    },
    emit() {
      this.$emit("input", { ...this.regra });
    },
    adicionarCaso() {
      this.regra.casos.push(novoCaso());
      this.emit();
    },
    duplicarUltimoCaso() {
      const ultimo = this.regra.casos[this.regra.casos.length - 1];
      if (!ultimo) return;
      this.regra.casos.push(JSON.parse(JSON.stringify(ultimo)));
      this.emit();
    },
    removerCaso(ci) {
      const caso = this.regra.casos[ci];
      if (!this.casoTemConteudo(caso)) {
        this.aplicarRemocaoCaso(ci);
        return;
      }
      this.confirmar(`Remover a Regra ${ci + 1}?`, "Todas as condições e destinos desta regra serão perdidos.")
        .then((ok) => { if (ok) this.aplicarRemocaoCaso(ci); });
    },
    aplicarRemocaoCaso(ci) {
      this.regra.casos.splice(ci, 1);
      this.$delete(this.avancadoPorCaso, ci);
      this.emit();
    },
    adicionarGrupo(ci) {
      this.regra.casos[ci].condicao.grupos.push(novoGrupo());
      this.emit();
    },
    removerGrupo(ci, gi) {
      const grupo = this.regra.casos[ci].condicao.grupos[gi];
      const temConteudo = (grupo.condicoes || []).some((c) => c.campo || c.valor);
      if (!temConteudo) {
        this.regra.casos[ci].condicao.grupos.splice(gi, 1);
        this.emit();
        return;
      }
      this.confirmar("Remover este grupo de condições?", "Todas as condições deste grupo serão perdidas.")
        .then((ok) => {
          if (ok) {
            this.regra.casos[ci].condicao.grupos.splice(gi, 1);
            this.emit();
          }
        });
    },
    alterarLogica(ci, gi, valor) {
      this.$set(this.regra.casos[ci].condicao.grupos[gi], "logica", valor);
      this.emit();
    },
    adicionarCondicao(ci, gi) {
      this.regra.casos[ci].condicao.grupos[gi].condicoes.push(novaCondicao());
      this.emit();
    },
    removerCondicao(ci, gi, ki) {
      this.regra.casos[ci].condicao.grupos[gi].condicoes.splice(ki, 1);
      this.emit();
    },
    adicionarCondicaoSimples(ci) {
      this.regra.casos[ci].condicao.grupos[0].condicoes.push(novaCondicao());
      this.emit();
    },
    removerCondicaoSimples(ci, ki) {
      this.regra.casos[ci].condicao.grupos[0].condicoes.splice(ki, 1);
      this.emit();
    },
    atualizarEntao(ci, expr) {
      this.$set(this.regra.casos[ci], "entao", expr);
      this.emit();
    },
    atualizarSenao(expr) {
      this.$set(this.regra, "senao", expr);
      this.emit();
    },
    isMultiSaida(raw) {
      return Array.isArray(raw);
    },
    expressaoTemValor(expr) {
      if (!expr || typeof expr !== "object") return false;
      const v = expr.valor != null ? String(expr.valor).trim() : "";
      const c = expr.caminho != null ? String(expr.caminho).trim() : "";
      return v.length > 0 || c.length > 0;
    },
    getSaidas(raw) {
      if (Array.isArray(raw)) {
        // Array vazio é estado patológico de multi-saída: renderiza uma linha
        // já populada com o paraValue legado e expressão vazia para o usuário
        // poder editar. Os handlers (atualizarSaidaExpressao, removerSaidaCaso)
        // detectam isMultiSaida e tratam corretamente.
        if (raw.length === 0) {
          return [{ para: this.paraValue || "", expressao: { tipo: "literal", valor: "" } }];
        }
        return raw;
      }
      return [{ para: "", expressao: raw || { tipo: "literal", valor: "" } }];
    },
    paraDoSaida(raw, si) {
      if (this.isMultiSaida(raw)) {
        return (raw[si] && raw[si].para) || "";
      }
      return this.paraValue;
    },
    atualizarSaidaExpressao(ci, si, novaExpr) {
      const entao = this.regra.casos[ci].entao;
      if (this.isMultiSaida(entao)) {
        const saidas = entao.slice();
        saidas[si] = { ...saidas[si], expressao: novaExpr };
        this.atualizarEntao(ci, saidas);
      } else {
        this.atualizarEntao(ci, novaExpr);
      }
    },
    atualizarSaidaPara(ci, si, novoPara) {
      const entao = this.regra.casos[ci].entao;
      if (this.isMultiSaida(entao)) {
        const saidas = entao.slice();
        saidas[si] = { ...saidas[si], para: novoPara };
        this.atualizarEntao(ci, saidas);
      } else {
        this.$emit("update:paraValue", novoPara);
      }
    },
    adicionarSaidaCaso(ci) {
      const entao = this.regra.casos[ci].entao;
      let saidas;
      if (this.isMultiSaida(entao)) {
        saidas = entao.slice();
      } else {
        // Converter forma legada → multi neste caso apenas. NÃO mexemos no
        // paraValue compartilhado pois ele continua valendo para os demais
        // casos legados (ex: regras duplicadas que ainda usam o destino
        // padrão da linha).
        saidas = [
          {
            para: this.paraValue || "",
            expressao: entao || { tipo: "literal", valor: "" },
          },
        ];
      }
      saidas.push({ para: "", expressao: { tipo: "literal", valor: "" } });
      this.atualizarEntao(ci, saidas);
    },
    removerSaidaCaso(ci, si) {
      const entao = this.regra.casos[ci].entao;
      if (!this.isMultiSaida(entao)) return;
      const saidas = entao.slice();
      saidas.splice(si, 1);
      if (saidas.length === 1 && !saidas[0].para) {
        // colapsa para forma legada
        this.atualizarEntao(ci, saidas[0].expressao);
      } else {
        this.atualizarEntao(ci, saidas);
      }
    },
    atualizarSaidaSenaoExpressao(si, novaExpr) {
      const senao = this.regra.senao;
      if (this.isMultiSaida(senao)) {
        const saidas = senao.slice();
        saidas[si] = { ...saidas[si], expressao: novaExpr };
        this.atualizarSenao(saidas);
      } else {
        this.atualizarSenao(novaExpr);
      }
    },
    atualizarSaidaSenaoPara(si, novoPara) {
      const senao = this.regra.senao;
      if (this.isMultiSaida(senao)) {
        const saidas = senao.slice();
        saidas[si] = { ...saidas[si], para: novoPara };
        this.atualizarSenao(saidas);
      } else {
        this.$emit("update:paraValue", novoPara);
      }
    },
    adicionarSaidaSenao() {
      const senao = this.regra.senao;
      let saidas;
      if (this.isMultiSaida(senao)) {
        saidas = senao.slice();
      } else {
        saidas = [
          {
            para: this.paraValue || "",
            expressao: senao || { tipo: "literal", valor: "" },
          },
        ];
      }
      saidas.push({ para: "", expressao: { tipo: "literal", valor: "" } });
      this.atualizarSenao(saidas);
    },
    removerSaidaSenao(si) {
      const senao = this.regra.senao;
      if (!this.isMultiSaida(senao)) return;
      const saidas = senao.slice();
      saidas.splice(si, 1);
      if (saidas.length === 1 && !saidas[0].para) {
        this.atualizarSenao(saidas[0].expressao);
      } else {
        this.atualizarSenao(saidas);
      }
    },
  },
};
</script>

<style scoped>
.regra-condicional {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.header-info {
  font-size: 12px;
  color: var(--text-muted);
  background: var(--bg-subtle);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 8px 12px;
}
.header-info .fa-icon, .header-info svg {
  color: var(--primary);
  margin-right: 6px;
}
.regra-bloco {
  border: 1px solid var(--border-medium);
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  margin-bottom: 8px;
}
.regra-bloco--dragging { opacity: 0.4; }
.drag-handle {
  cursor: grab;
  color: var(--text-subtle);
  padding: 2px 4px;
  font-size: 12px;
}
.drag-handle:active { cursor: grabbing; }
.regra-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.regra-titulo {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-darkest);
  flex: 0 0 auto;
}
.modo-segmented {
  display: inline-flex;
  border: 1px solid var(--border-medium);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  flex: 0 0 auto;
}
.segmented-btn {
  background: transparent;
  border: none;
  padding: 3px 10px;
  font-size: 11px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.15s;
}
.segmented-btn + .segmented-btn { border-left: 1px solid var(--border-medium); }
.segmented-btn:hover { background: var(--bg-subtle); }
.segmented-btn--active {
  background: var(--primary);
  color: #fff;
  font-weight: 600;
}
.segmented-btn--active:hover { background: var(--primary); }
.header-trash { margin-left: auto; }
.bloco-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}
.quando-bloco, .enviar-bloco {
  margin-top: 6px;
}
.enviar-bloco {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--border-default);
}
.cond-lista {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cond-linha {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cond-conector {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-subtle);
  width: 24px;
  text-align: center;
}
.cond-campo { flex: 2; }
.cond-op { flex: 1.2; }
.cond-tipo-valor { flex: 1; }
/* Mesmo tom do placeholder "Não aplicável" do campo de valor ao lado. */
.cond-tipo-valor:disabled { color: #8a93a2; }
.cond-valor { flex: 1.5; }
.btn-add-inline {
  align-self: flex-start;
  margin-top: 2px;
}
.btn-dashed {
  border-style: dashed;
  align-self: flex-start;
}
.btn-fallback-add {
  align-self: flex-start;
}
.acoes-casos {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-icon {
  padding: 0.125rem 0.4rem;
  line-height: 1;
}
.modo-avancado {
  background: #fafbff;
  padding: 8px;
  border-radius: 6px;
  border: 1px dashed #dddce9;
}
.grupo-bloco {
  background: #fff;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 8px;
}
.grupo-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #475467;
}
.grupo-prefix { font-weight: 500; }
.logica-select { max-width: 240px; }
.fallback-bloco {
  margin-top: 8px;
  margin-left: 16px;
  padding: 12px;
  border: 1px solid var(--border-default);
  border-left: 3px solid var(--text-subtle);
  border-radius: 6px;
  background: var(--bg-subtle);
}
.fallback-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.fallback-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--text-muted);
}
.fallback-label ::v-deep svg { color: var(--text-subtle); }
.de-para-linha {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.de-para-col {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.de-para-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding-top: 34px;
  flex: 0 0 auto;
  min-width: 26px;
}
.de-para-input-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.de-para-col--para .de-para-input-wrap {
  padding-top: 26px;
}
.de-para-seta {
  flex: 0 0 auto;
  color: var(--text-subtle);
  font-size: 14px;
  padding-top: 36px;
}

/* Mobile: empilha De/seta/Para em vez de deixar lado a lado */
@media (max-width: 767.98px) {
  .de-para-linha {
    flex-direction: column;
    gap: 12px;
  }
  .de-para-col {
    width: 100%;
  }
  .de-para-label {
    padding-top: 0;
    min-width: 0;
  }
  .de-para-col--para .de-para-input-wrap {
    padding-top: 0;
  }
  .de-para-seta {
    padding-top: 0;
    align-self: center;
    transform: rotate(90deg);
  }
  .cond-linha {
    flex-wrap: wrap;
    gap: 4px;
  }
  .cond-linha .cond-campo,
  .cond-linha .cond-op,
  .cond-linha .cond-tipo-valor,
  .cond-linha .cond-valor {
    flex: 1 1 100%;
  }
  .cond-conector {
    width: auto;
  }
}
.btn-selecionar-inline {
  font-size: 11px;
  align-self: flex-start;
}
.acoes-para {
  display: flex;
  align-items: center;
  gap: 4px;
}
.btn-add-saida {
  margin-top: 8px;
}
.enviar-bloco .de-para-linha + .de-para-linha {
  margin-top: 8px;
}
.fallback-bloco .de-para-linha + .de-para-linha {
  margin-top: 8px;
}
</style>
